/**
 * Connection adapter hooks for the unified ConnectionGroup component.
 *
 * Each adapter provides the provider-specific behaviors (status derivation,
 * sync triggers, available syncs) that ConnectionGroup consumes via props.
 *
 * To add a new native integration (e.g. Salesforce):
 * 1. Add the provider to NATIVE_PROVIDERS in types.ts
 * 2. useNativeConnectionAdapter already handles it generically
 */

import { useCallback, useMemo, type ReactNode } from 'react';
import type { TableConnector, NangoConnectorConfig, HubspotDirectConnectorConfig } from '@loader/shared-types';

import {
  useConnectionSyncsQuery,
  useNangoIntegrations,
  useTriggerNangoSync,
  usePauseNangoSync,
  useResumeNangoSync,
} from './useNangoConnectors';
import { useSyncTrigger, useConnectorToggle } from './useSyncTrigger';
import { toSyncStatus } from '../components/shared';
import type { SyncStatus } from '../components/shared';
import { NATIVE_PROVIDERS, CONNECTOR_META } from '../types';
import type { AvailableSync } from '../types';

import { useToastStore } from '@/store';
import type { Table } from '@/lib/types';

// ============================================================================
// Shared adapter result interface
// ============================================================================

export interface ConnectionAdapterResult {
  icon: ReactNode;
  label: string;
  availableSyncs: AvailableSync[] | null;
  getSyncKey: (connector: TableConnector) => string | undefined;
  getStatus: (connector: TableConnector, table: Table | undefined) => SyncStatus;
  onTriggerSync: (tableId: string) => void;
  onPause: (tableId: string) => void;
  onResume: (tableId: string) => void;
  showLastSyncedAt: boolean;
}

// ============================================================================
// Nango adapter
// ============================================================================

/**
 * Adapter for Nango-based connections.
 *
 * Fetches available syncs from the Nango API and maps status from Nango's
 * sync state machine (RUNNING/SUCCESS/PAUSED/ERROR).
 */
export function useNangoConnectionAdapter(
  connectionId: string,
  providerConfigKey: string,
): ConnectionAdapterResult {
  const { data: connectionSyncs } = useConnectionSyncsQuery(connectionId, providerConfigKey);
  const { data: integrations } = useNangoIntegrations();
  const triggerSync = useTriggerNangoSync();
  const pauseSync = usePauseNangoSync();
  const resumeSync = useResumeNangoSync();
  const toast = useToastStore();

  const integration = useMemo(
    () => integrations?.find((i) => i.uniqueKey === providerConfigKey),
    [integrations, providerConfigKey],
  );

  const syncStatusMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const sync of connectionSyncs ?? []) {
      map.set(sync.name, sync.status);
    }
    return map;
  }, [connectionSyncs]);

  const icon: ReactNode = useMemo(
    () =>
      integration?.logo ? (
        <img
          src={integration.logo}
          alt=""
          className="h-6 w-6 shrink-0 rounded-full border object-contain p-0.5"
        />
      ) : (
        <div className="bg-muted h-6 w-6 shrink-0 rounded-full border" />
      ),
    [integration?.logo],
  );

  const label =
    integration?.displayName ??
    providerConfigKey
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  const availableSyncs = useMemo(
    () => connectionSyncs?.map((s) => ({ key: s.name, label: s.name })) ?? null,
    [connectionSyncs],
  );

  const getSyncKey = useCallback(
    (conn: TableConnector) => (conn.config as NangoConnectorConfig).nango_sync_name,
    [],
  );

  const getStatus = useCallback(
    (connector: TableConnector, _table: Table | undefined) => {
      const config = connector.config as NangoConnectorConfig;
      const providerStatus = syncStatusMap.get(config.nango_sync_name ?? '');
      return toSyncStatus({ enabled: connector.enabled, providerStatus });
    },
    [syncStatusMap],
  );

  const onTriggerSync = useCallback(
    (tableId: string) => {
      triggerSync.mutate(tableId, {
        onSuccess: () => toast.success('Sync triggered - new data will appear as a new version'),
        onError: () => toast.error('Failed to trigger sync'),
      });
    },
    [triggerSync, toast],
  );

  const onPause = useCallback(
    (tableId: string) => {
      pauseSync.mutate(tableId, {
        onSuccess: () => toast.success('Sync paused'),
        onError: () => toast.error('Failed to pause sync'),
      });
    },
    [pauseSync, toast],
  );

  const onResume = useCallback(
    (tableId: string) => {
      resumeSync.mutate(tableId, {
        onSuccess: () => toast.success('Sync resumed'),
        onError: () => toast.error('Failed to resume sync'),
      });
    },
    [resumeSync, toast],
  );

  return {
    icon,
    label,
    availableSyncs,
    getSyncKey,
    getStatus,
    onTriggerSync,
    onPause,
    onResume,
    showLastSyncedAt: false,
  };
}

// ============================================================================
// Native adapter
// ============================================================================

/**
 * Adapter for native (first-party) integration connections.
 *
 * Works for any provider registered in NATIVE_PROVIDERS (HubSpot, future SFDC, etc.).
 * Available syncs come from the static provider definition; status is derived
 * from connector fields + local syncing indicator.
 */
export function useNativeConnectionAdapter(provider: string): ConnectionAdapterResult {
  const { handleTriggerSync, syncingTableIds } = useSyncTrigger();
  const { handlePause, handleResume } = useConnectorToggle();

  const providerDef = NATIVE_PROVIDERS[provider];
  const meta = providerDef ? CONNECTOR_META[providerDef.connectorType] : undefined;

  const icon: ReactNode = useMemo(
    () =>
      meta?.logo ? (
        <img src={meta.logo} alt="" className="h-5 w-5 shrink-0 rounded-full object-contain" />
      ) : meta?.icon ? (
        <meta.icon className="text-muted-foreground h-5 w-5 shrink-0" />
      ) : null,
    [meta],
  );

  const availableSyncs = useMemo(() => providerDef?.objectTypes ?? [], [providerDef]);

  const getSyncKey = useCallback(
    (conn: TableConnector) => (conn.config as HubspotDirectConnectorConfig).object_type,
    [],
  );

  const getStatus = useCallback(
    (connector: TableConnector, _table: Table | undefined) => {
      const isSyncing = syncingTableIds.has(connector.table_id);
      return toSyncStatus({ enabled: connector.enabled, isSyncing, lastSyncedAt: connector.last_synced_at });
    },
    [syncingTableIds],
  );

  return {
    icon,
    label: meta?.label ?? provider,
    availableSyncs,
    getSyncKey,
    getStatus,
    onTriggerSync: handleTriggerSync,
    onPause: handlePause,
    onResume: handleResume,
    showLastSyncedAt: true,
  };
}
